import { KeyValue, KeyValuePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, TemplateRef, ViewChild, signal } from '@angular/core';
import { NgbAccordionDirective, NgbAccordionItem, NgbAccordionModule, NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Backend } from 'src/app/backend-interface/backend';
import { Client, ClientApplication, RefPolicy, RequestRefPolicy } from 'src/app/service/global.service';
import { ToastService } from 'src/app/service/toast.service';
import { TranslationService } from 'src/app/service/translation.service';
import { FormClientAppSettingsComponent } from './form-client-app-settings/form-client-app-settings.component';
import { FormRefPoliciesSettingsComponent } from '../ref-policies/form-ref-policies-settings/form-ref-policies-settings.component';
import { LoadingErrorComponent } from 'src/app/components/loading-error/loading-error.component';
import { ToastComponent } from 'src/app/components/toast/toast.component';

@Component({
  selector: 'app-clients-settings',
  templateUrl: './clients-settings.component.html',
  styleUrls: ['./clients-settings.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    KeyValuePipe,
    FormClientAppSettingsComponent,
    FormRefPoliciesSettingsComponent,
    LoadingErrorComponent,
    ToastComponent,
    NgbModalModule,
    NgbAccordionModule,
    NgbAccordionDirective
  ],
  providers: [NgbModal, TranslationService, ToastService]
})
export class ClientsSettingsComponent implements OnInit {
  readonly lClients = signal<Client[] | undefined>(undefined);
  readonly lClientApplications = signal<ClientApplication[]>([]);
  readonly preId = signal<string>('');
  readonly clientSelected = signal<Client | undefined>(undefined);
  readonly mapClient = signal<Map<string, ClientApplication[]>>(new Map());
  readonly error = signal<HttpErrorResponse | undefined>(undefined);
  readonly lRefPolicies = signal<RefPolicy[]>([]);
  readonly loadingOerror = signal<boolean>(true);
  readonly appSelected = signal<ClientApplication | undefined>(undefined);

  /** Call from the html the template which have errordeleteapp in id. Use in case of delete app error*/
  @ViewChild('errordeleteapp') error_app!: TemplateRef<any>;

  /** Call from the html the template which have errordeleteclient in id. Use in case of delete client error**/
  @ViewChild('errordeleteclient') error_client!: TemplateRef<any>;

  readonly translationService = inject(TranslationService);
  readonly toastService = inject(ToastService);
  readonly modalService = inject(NgbModal);

  @ViewChild('clientsItem') set clientsItemRef(item: NgbAccordionItem | undefined) {
    if (item && this.clientsItem !== item) {
      this.clientsItem = item;
      this.clientsItem.disabled = true;
      if (this.clientsItem.collapsed) {
        this.clientsItem.toggle();
      }
    }
  }
  private clientsItem?: NgbAccordionItem;

  @ViewChild('applicationsItem') set applicationsItemRef(item: NgbAccordionItem | undefined) {
    if (item && this.applicationsItem !== item) {
      this.applicationsItem = item;
      this.applicationsItem.disabled = true;
      if (this.applicationsItem.collapsed) {
        this.applicationsItem.toggle();
      }
    }
  }
  private applicationsItem?: NgbAccordionItem;

  @ViewChild('refPoliciesItem') set refPoliciesItemRef(item: NgbAccordionItem | undefined) {
    if (item && this.refPoliciesItem !== item) {
      this.refPoliciesItem = item;
      this.refPoliciesItem.disabled = true;
      if (this.refPoliciesItem.collapsed) {
        this.refPoliciesItem.toggle();
      }
    }
  }
  private refPoliciesItem?: NgbAccordionItem;


  ngOnInit(): void {
    this.loadingOerror.set(true);
    Backend.POLICIES.getClients()
      .then(async clients => {
        this.lClients.set(clients);
        await this.loading(0.5);
      })
      .catch((e: any) => {
        this.loadingOerror.set(false);
      });
  }
  async loading(n: number) {
    await new Promise(r => setTimeout(r, n * 1000));
    this.loadingOerror.set(false);
  }

  getClientAppFromOs() {
    const map = new Map<string, ClientApplication[]>();
    for (let os of Backend.POLICIES.getAllOsClientApplications(this.lClientApplications())) {
      map.set(os, Backend.POLICIES.getAppClientByOS(os, this.lClientApplications()));
    }
    this.mapClient.set(map);
  }

  originalOrder = (a: KeyValue<string, ClientApplication[]>, b: KeyValue<string, ClientApplication[]>): number => 0;

  /**
   * Attribute var client to clientSelected and show the hidden part of the html
   *
   * @param client
   */
  public async clientSelectionnee(client: Client) {
    const apps = await Backend.POLICIES.getApplicationClient(client);
    this.lClientApplications.set(apps);
    this.mapClient.set(new Map());
    this.getClientAppFromOs();
    const refPolicies = (await Backend.REF_POLICIES.getClientRefPolicies(client)).filter(elem => elem.type === "client");
    this.lRefPolicies.set(refPolicies);
    this.clientSelected.set(client);

    let del = document.getElementById("actifElem");
    if (del) {
      del.removeAttribute('id');
      del.setAttribute('id', this.preId());
    }
    let el = document.getElementById("elem" + client.id);
    if (el) {
      el.id = "actifElem";
      this.preId.set("elem" + client.id);
    }
    document.getElementById('clientApplications')?.removeAttribute('hidden');
  }


  /**
   * Allow to hidden again the hidden part which was showing
   */
  hideClientDetails() {
    const el = document.getElementById('clientApplications');
    if (el) {
      el.removeAttribute('hidden');
      el.setAttribute('hidden', 'hidden');
    }
  }

  /** allow to scroll immediatly at the HTML element concerned */
  public scroolTo() {
    (document.getElementById('actifElem') as HTMLElement)?.scrollIntoView({ behavior: "smooth", block: "end", inline: "end" });
  }


  public async addClient(client: Client) {
    try {
      await Backend.POLICIES.addClient(client);
      this.translationService.get('security-policies-toast-add').subscribe(msg => {
        this.toastService.show(`${client.name} ${msg}`, { classname: 'bg-success', delay: 6000 });
      });
      this.hideClientDetails();
      this.ngOnInit();
    } catch (e) {
      const err = await e as HttpErrorResponse;
      this.toastService.show(err.error.message, { classname: 'bg-danger', delay: 5000 });
    }
  }

  public async addApplication(app: ClientApplication) {
    app.clientId = this.clientSelected()?.id;
    try {
      await Backend.POLICIES.addApplication(app);
      this.translationService.get('security-policies-toast-add').subscribe(msg => {
        this.toastService.show(`${app.packageName} ${msg}`, { classname: 'bg-success', delay: 6000 });
      });
      this.hideClientDetails();
      this.ngOnInit();
    } catch (e) {
      const err = await e as HttpErrorResponse;
      this.toastService.show(err.error.message, { classname: 'bg-danger', delay: 5000 });
    }
  }

  public async deleteClient(client: Client) {
    try {
      const apps = await Backend.POLICIES.getApplicationClient(client);
      this.lClientApplications.set(apps);
      if (!this.errorAppPolicy()) {
        for (let app of this.lClientApplications()) {
          await this.deleteApplication(app);
        }
      }
      await Backend.POLICIES.deleteClient(client);
      this.translationService.get('security-policies-toast-delete').subscribe(msg => {
        this.toastService.show(`${client.name} ${msg}`, { classname: 'bg-success', delay: 6000 });
      });
      this.hideClientDetails();
      this.ngOnInit();
    } catch (e) {
      this.error.set(await e as HttpErrorResponse);
      this.clientSelectionnee(client);
      this.modalService.open(this.error_client, { centered: true });
    }
  }

  public async deleteApplication(app: ClientApplication) {
    try {
      await Backend.POLICIES.deleteApplication(app);
      this.translationService.get('security-policies-toast-delete').subscribe(msg => {
        this.toastService.show(`${app.packageName} ${msg}`, { classname: 'bg-success', delay: 6000 });
      });
      this.hideClientDetails();
      this.ngOnInit();
    } catch (e) {
      this.appSelected.set(app);
      this.modalService.open(this.error_app, { centered: true });
      const err = await e as HttpErrorResponse;
      this.toastService.show(err.error?.message || 'An error occurred while deleting the application.', { classname: 'bg-danger', delay: 5000 });
    }
  }

  public errorAppPolicy() {
    return this.lClientApplications().find(elem => elem.policy && elem.policy.length > 0);
  }

  public async addRefPolicy(refPolicy: RequestRefPolicy) {
    try {
      const client = this.clientSelected();
      if (client) {
        await Backend.REF_POLICIES.addRefPolicyToClient(client, refPolicy);
        this.translationService.get('security-policies-toast-add').subscribe(msg => {
          this.toastService.show(`${refPolicy.name} ${msg}`, { classname: 'bg-success', delay: 5000 });
        });
        this.hideClientDetails();
        this.ngOnInit();
      } else {
        this.toastService.show("Problem with selected client, please try again", { classname: 'bg-danger', delay: 5000 });
      }
    } catch (e) {
      const err = await e as HttpErrorResponse;
      this.toastService.show(err.error.message, { classname: 'bg-danger', delay: 5000 });
    }
  }

  public async deleteRefPolicy($event: RefPolicy) {
    try {
      const client = this.clientSelected();
      if (client) {
        await Backend.REF_POLICIES.deleteRefPolicyFromClient(client, $event);
        this.translationService.get('security-policies-toast-delete').subscribe(msg => {
          this.toastService.show(`${$event.name} ${msg}`, { classname: 'bg-success', delay: 5000 });
        });
        this.hideClientDetails();
        this.ngOnInit();
      } else {
        this.toastService.show("Problem with selected client, please try again", { classname: 'bg-danger', delay: 5000 });
      }
    } catch (e) {
      const err = await e as HttpErrorResponse;
      this.toastService.show(err.error.message, { classname: 'bg-danger', delay: 5000 });
    }
  }

  public async patchRefPolicy($event: RequestRefPolicy) {
    try {
      const client = this.clientSelected();
      if (client) {
        await Backend.REF_POLICIES.patchRefPolicyToClient(client, $event);
        this.translationService.get('ref-policies-edit-success').subscribe(msg => {
          this.toastService.show(`${$event.name} ${msg}`, { classname: 'bg-success', delay: 5000 });
        });
        this.hideClientDetails();
        this.ngOnInit();
      } else {
        this.toastService.show("Problem with selected client, please try again", { classname: 'bg-danger', delay: 5000 });
      }
    } catch (e) {
      const err = await e as HttpErrorResponse;
      this.toastService.show(err.error.message, { classname: 'bg-danger', delay: 5000 });
    }
  }

}


